<script setup>
import {computed, defineProps, defineEmits} from "vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { convertTimestampToData } from "@/tools/date-time-tools.js";
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import { rubricColor } from "@/tools/rubric-colors";
import GoodIcon from "@/components/Icons/GoodIcon.vue";
import SadIcon from "@/components/Icons/SadIcon.vue";
import NeutralIcon from "@/components/Icons/NeutralIcon.vue";
import { t } from "@/controllerLK/AvalLocalization";
import SaveIcon from "@/components/Icons/SaveIcon.vue";

const props = defineProps({
  data: {
    type: Object,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
  boxWidth: {
    type: Number,
    required: true
  },
  isToolbarActive: {
    type: Boolean,
    default: false
  },
  chosenCardList: {
    type: Array,
    required: true,
    default: () => []
  }
});

const emit = defineEmits(["update:chosen-card-list"]);

// Прокси-computed для v-model на отмеченные карточки
const proxyChosenCardList = computed({
  get() {
    return props.chosenCardList;
  },
  set(newList) {
    emit("update:chosen-card-list", newList);
  }
});

const newsFeedStore = useNewsFeedStore();
const rubricsStore = useRubricsStore();

const addToFavorite = () => {
  newsFeedStore.items[props.index].is_favourite = !newsFeedStore.items[props.index].is_favourite;
  newsFeedStore.setFavorite(props.data.id, newsFeedStore.items[props.index].is_favourite);
};

function getRubricCard() {
  if (newsFeedStore.currentNews.rubric_ids) {
    const rubricIdsSet = new Set(newsFeedStore.currentNews.rubric_ids);
    rubricsStore.currentCardRubrics = rubricsStore.allRubrics.filter(rubric => rubricIdsSet.has(rubric.id));
  }
}

let timeout;

function debounce(func, delay) {
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), delay);
  };
}

const debouncedGetRubricCard = debounce(getRubricCard, 100);

// Установить активную новость.
const setCurrentNews = async () => {
  if (newsFeedStore.downloadOn) {
    if (newsFeedStore.downloadList.find(item => item === props.data.id)) {
      newsFeedStore.downloadList = newsFeedStore.downloadList.filter(item => item !== props.data.id);
    } else {
      newsFeedStore.downloadList.push(props.data.id);
    }
    return;
  }
  // Сравниваем по ссылке: id-ы у карточек могут дублироваться/отсутствовать (мэппинг из API),
  // поэтому проверка по .id пропускала клик на середину и правая панель оставалась на первой.
  if (newsFeedStore.currentNews === props.data) return;
  rubricsStore.currentCardRubrics = [];
  newsFeedStore.currentNews = props.data;
  debouncedGetRubricCard();
};

// Является ли текущая новостная карточка активной
const isCurrentCardActive = computed(() =>
( newsFeedStore.currentNews?.id === props.data.id && !!newsFeedStore.currentNews?.id ));

// Упрощенная логика для состояний кнопки избранного
const favoriteButtonClasses = computed(() => ({
  "newsfeed-item__button--favorite": props.data.is_favourite,
  "newsfeed-item__button--active-card": isCurrentCardActive.value
}));

const favoriteIconClasses = computed(() => ({
  "newsfeed-item__button-icon--favorite": props.data.is_favourite,
  "newsfeed-item__button-icon--active-card": isCurrentCardActive.value
}));

// Получение наименования значимости
const getImportanceName = (importance) => {
  if (importance === 1) return "green";
  if (importance === 2) return "yellow";
  if (importance === 3) return "orange";
  if (importance === 4) return "red";
  return "gray";
};

// Тултип счётчика рубрик — имена подчёркнуты своими цветами (тот же цвет,
// которым подсвечиваются matched_terms этой рубрики в правой панели).
const rubricsTooltip = computed(() => {
  const list = (props.data.rubrics || []).filter((r) => r?.rubric_name);
  if (!list.length) return "";
  return list
    .map((r, index) => {
      const color = rubricColor(index);
      const name = escapeTooltipHtml(r.rubric_name);
      return `<span style="text-decoration:underline solid ${color};text-decoration-thickness:2px;text-underline-offset:3px">${name}</span>`;
    })
    .join(", ");
});

function escapeTooltipHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Получение лейбла значимости
const getImportanceLabel = (importance) => {
  if (importance === 1) return "not_critical";
  if (importance === 2) return "moderately";
  if (importance === 3) return "important";
  if (importance === 4) return "critical";
  return "unset";
};

</script>
<template>
  <div
    class="newsfeed-item"
    :class="{'newsfeed-item--active': isCurrentCardActive,
             'newsfeed-item--chosen': chosenCardList.includes(props.data.id)}"
    @click.stop="setCurrentNews"
  >
    <div
      class="newsfeed-item__header"
      :class="{'newsfeed-item__header-selector--active': isToolbarActive}"
    >
      <!--    Чек-бокс для отметки новостей при включенном тулбаре    -->
      <div
        v-if="isToolbarActive"
        class="newsfeed-item__header-selector"
      >
        <AvalCheckbox
          v-model="proxyChosenCardList"
          :value="props.data.id"
        />
      </div>
      <!--    Заголовок    -->
      <div
        v-dompurify-html="props.data.title"
        class="newsfeed-item__header-title"
      />
      <!--    Кнопка добавления в избранное    -->
      <button
        v-if="rubricsStore.chosenRubrics.length >= 0 && rubricsStore.isCurrentRubricFixed()"
        class="newsfeed-item__button"
        :class="favoriteButtonClasses"
        @click="addToFavorite"
      >
        <SaveIcon
          class="newsfeed-item__button-icon"
          :class="favoriteIconClasses"
        />
      </button>
    </div>
    <div
      v-dompurify-html="props.data.text"
      class="newsfeed-item__body"
    />
    <div class="newsfeed-item__footer">
      <div
        v-if="rubricsStore.chosenRubrics.length >= 0 && rubricsStore.isCurrentRubricFixed()"
        class="newsfeed-item__footer-meta"
      >
        <!--    Сантимент    -->
        <Component
          :is="props.data.sentiment > 0 ? GoodIcon : props.data.sentiment < 0 ? SadIcon : NeutralIcon"
          v-if="props.data.sentiment !== null"
          class="newsfeed-item__button-sentiment"
          :active="isCurrentCardActive"
          :news="true"
        />
        <!--    Значимость    -->
        <AvalChip
          v-if="props.data.importance && props.data.importance > 0"
          :model-value="true"
          :use-cross-icon="false"
          :use-hover="false"
          :color="getImportanceName(props.data.importance)"
        >
          {{ t('news_page_body_news_feed_item.importance.' + getImportanceLabel(props.data.importance)) }}
        </AvalChip>
        <!-- Названия городов пока не приходят с ms.cards, чипс скрыт до появления фичи -->
        <AvalChip
          v-if="props.data.cities?.length && props.data.cities[0]?.city"
          :use-cross-icon="false"
          max-width="180px"
          :use-hover="false"
          :color="isCurrentCardActive ? 'gray' : ''"
          type="text"
          :use-location-icon="true"
        >
          {{ props.data.cities[0].city }}
        </AvalChip>
        <!-- Статья попала более чем в одну рубрику текущего проекта — счётчик с тултипом. -->
        <AvalChip
          v-if="props.data.rubrics?.length > 1"
          v-tooltip:bottom="rubricsTooltip"
          :use-cross-icon="false"
          :use-hover="false"
          :color="isCurrentCardActive ? 'gray' : ''"
          type="text"
        >
          в {{ props.data.rubrics.length }} рубриках
        </AvalChip>
      </div>
      <div class="newsfeed-item__footer-date">
        {{ convertTimestampToData(props.data.date) }}
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.newsfeed-item {
  height: 205px; // По макету высота 195px, но в таком случае не помещается 3 строки текста. Уточнить у дизайнеров.
  min-height: 202px; // Задача 2026 
  box-shadow: 0 4px 8px 0 #00000024;
  background: var(--background-bg-white);
  border-radius: 10px;
  padding: 20px;
  display: flex;
  gap: 20px;
  flex-direction: column;
  cursor: default;

  &__button {
    width: 36px;
    height: 36px;
    border-radius: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(--fix-button-secondary-inactive-default);

    &:hover {
      background-color: var(--fix-button-secondary-inactive-hover);
    }

    &--favorite {
      background-color: var(--fix-button-secondary-active-default);

      &:hover {
        background-color: var(--fix-button-secondary-active-hover);
      }
    }

    &--active-card {
      // Стили для активной карточки
      background-color: var(--fix-button-dark-inactive-default);

      &:hover {
        background-color: var(--fix-button-dark-inactive-hover);
      }
    }

    // Комбинированное состояние: активная карточка + избранное
    &.newsfeed-item__button--favorite.newsfeed-item__button--active-card {
      background-color: var(--fix-button-dark-active-default);

      &:hover {
        background-color: var(--fix-button-dark-active-hover);
      }
    }

    &-icon {
      min-width: 17px;
      min-height: 17px;
      color: var(--icon-icon-primary);
      
      &--favorite {
        fill: var(--icon-icon-primary);
        color: var(--icon-icon-primary);
      }

      &--active-card {
        // Стили для активной карточки
        color: var(--fix-button-dark-inactive-icon);
      }

      // Комбинированное состояние: активная карточка + избранное
      &.newsfeed-item__button-icon--favorite.newsfeed-item__button-icon--active-card {
        fill: var(--fix-button-dark-inactive-icon);
        color: var(--fix-button-dark-inactive-icon);
      }
    }

    &-sentiment {
      width: 20px;
      height: 20px;
    }
  }

  &:hover {
    background: var(--background-bg-active-hover);
  }

  &__body {
    font-size: 14px;
    line-height: 18px; // (3 строки текста) В макете разная информация, где то 14, где то 18.
    font-weight: 400;
    color: var(--text-text-main);
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    text-overflow: ellipsis;
    min-height: 54px; // Задача 2026
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &-meta {
      display: flex;
      gap: 6px;
      align-items: center;
    }

    &-date {
      color: #898989;
      font-size: 12px;
      font-weight: 400;
      margin-left: auto;
    }
  }

  &__header {
    display: grid;
    grid-template-columns: 1fr 36px;
    gap: 10px;
    align-items: center;
    min-height: 44px; // Задача 2026

    &-selector {
      width: 20px;
      height: 20px;

      &--active {
          grid-template-columns: 20px 1fr 36px;
      }

      input {
        display: none;
      }

      label {
        cursor: pointer;
      }
    }

    &-title {
      color: var(--text-text-main);
      font-weight: 500;
      font-size: 16px;
      line-height: 22px;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      overflow: hidden;
      -webkit-line-clamp: 2;
      line-clamp: 2;
    }
  }

  // Стили для отмеченных карточек
  &--chosen {
    outline-offset: -1.5px;
    outline: 1.5px solid #1F9AFF;
  }

  // Стили для активной карточки.
  &--active {
    background: #7b87a5;
    color: var(--text-text-on-inverse);

    &:hover {
      background: #7b87a5;
      color: var(--text-text-on-inverse);
    }

    .newsfeed-item__header-title {
      color: var(--text-text-on-inverse)
    }

    .newsfeed-item__body {
      color: var(--text-text-on-inverse);
    }

    .newsfeed-item__footer {
      &-date {
        color: var(--text-text-on-inverse);
      }
    }

  }
}


</style>
